/**
 * What one card says about its takeover, as catalogue suffixes. Pure.
 *
 * The card has to answer three different people at once: the operator whose own
 * mobile is ringing ("answer it and press one"), a colleague watching the same
 * call ("somebody is taking this"), and everybody once it has happened. Getting
 * this wrong is not cosmetic - an operator who does not know their phone is
 * about to ring will not answer it.
 *
 * React-free, so it runs under plain node.
 */
import type { LiveCall } from "../../../lib/aiLive";

/** Suffixes under `aiCalls.live.takeover.state`. */
export const TAKEOVER_STATES = [
  "ringingMineMobile",
  "ringingMineBrowser",
  "ringingTheirs",
  "handed",
  "onMyMobile",
  "onMyBrowser",
  "onTheirs",
  "failedMine",
  "cancelledMine",
] as const;
export type TakeoverState = (typeof TAKEOVER_STATES)[number];

/**
 * Failures that were the operator's own choice: they pressed another key on the
 * mobile, hung it up, or called the browser attempt off. "It did not work" would
 * be the card blaming something for a decision.
 */
const CHOSEN = new Set(["declined", "operator_left"]);

/** What this card should say about the attempt, or null when there is nothing to say. */
export function takeoverState(call: Pick<LiveCall, "takeover" | "phase">): TakeoverState | null {
  const takeover = call.takeover;
  if (!takeover) return null;
  if (takeover.failure) {
    // Only the operator who tried is told: for everybody else a failed attempt
    // is a call the AI is still on, which is what the rest of the card says.
    // Both lines say the AI is still talking, so neither belongs on a card whose
    // call is over; the ending line says what became of it there.
    if (!takeover.mine || call.phase === "ended") return null;
    return CHOSEN.has(takeover.failure) ? "cancelledMine" : "failedMine";
  }
  switch (takeover.stage) {
    case "ringing":
    case "joined":
      if (!takeover.mine) return "ringingTheirs";
      return takeover.via === "browser" ? "ringingMineBrowser" : "ringingMineMobile";
    case "handed":
      return "handed";
    case "bridged":
      if (!takeover.mine) return "onTheirs";
      return takeover.via === "browser" ? "onMyBrowser" : "onMyMobile";
    default:
      return null;
  }
}

/**
 * Is an attempt on its way, so the card should offer nothing but waiting?
 *
 * "Take over" is hidden and so is "end the call": the AI is about to stop
 * talking, and a stop pressed in that second would hang up on an operator who
 * is already in the room.
 */
export function takeoverBusy(call: Pick<LiveCall, "takeover">): boolean {
  const takeover = call.takeover;
  if (!takeover || takeover.failure) return false;
  return takeover.stage === "ringing" || takeover.stage === "joined" || takeover.stage === "handed";
}

/** Has somebody taken this call: the AI is gone and two people are talking. */
export function takenOver(call: Pick<LiveCall, "takeover">): boolean {
  const takeover = call.takeover;
  return Boolean(takeover && !takeover.failure && (takeover.stage === "handed" || takeover.stage === "bridged"));
}
